import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SEGMENT_ENTITY_LABEL,
  SEGMENT_ENTITY_TYPES,
  defaultFilter,
  type FilterTree,
  type SegmentEntityType,
} from "@colab/shared";
import { FilterTreeBuilder } from "@/components/shared/saved-views";
import { useCreateSegment } from "@/features/settings/use-create-segment";

type Props = {
  defaultEntityType?: SegmentEntityType;
};

export function CreateSegmentSheet({ defaultEntityType = "partner" }: Props) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const mutation = useCreateSegment();

  const [entityType, setEntityType] =
    useState<SegmentEntityType>(defaultEntityType);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [filter, setFilter] = useState<FilterTree>(() =>
    defaultFilter(defaultEntityType),
  );

  function changeEntity(next: SegmentEntityType) {
    setEntityType(next);
    setFilter(defaultFilter(next));
  }

  function reset() {
    setEntityType(defaultEntityType);
    setName("");
    setDescription("");
    setFilter(defaultFilter(defaultEntityType));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate(
      {
        entity_type: entityType,
        name: name.trim(),
        description: description.trim() || null,
        filter,
      },
      {
        onSuccess: (res) => {
          toast.success("Segment created");
          setOpen(false);
          reset();
          if (res.id) navigate(`/admin/settings/segments/${res.id}`);
        },
      },
    );
  }

  const entityOptions = useMemo(
    () =>
      SEGMENT_ENTITY_TYPES.map((t) => ({
        value: t,
        label: SEGMENT_ENTITY_LABEL[t],
      })),
    [],
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <SheetTrigger asChild>
        <Button size="sm">
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          New segment
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight">
            Create segment
          </SheetTitle>
          <SheetDescription>
            Segments capture a reusable filter expression for one entity type
            (e.g. partners or deals). Once saved, they power Saved Views and
            audience scoping.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} className="mt-6 space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={120}
                placeholder="Gold tier active partners"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                Entity <span className="text-destructive">*</span>
              </Label>
              <Select
                value={entityType}
                onValueChange={(v) => changeEntity(v as SegmentEntityType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {entityOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-xs">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="What this segment captures. Visible to other admins."
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Filter</Label>
            <FilterTreeBuilder
              entityType={entityType}
              value={filter}
              onChange={setFilter}
            />
          </div>

          <div className="sticky bottom-0 -mx-6 flex justify-end gap-2 border-t bg-card px-6 py-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create segment
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
